import React from 'react'
import { useQuery } from 'react-query'
import {
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  Input,
  Text,
  Button
} from '@chakra-ui/react'
import Axios from '../../Axios'

export default function PartnerHierarchyFields({
  values,
  onChange,
  onSave,
  isSaving,
  canSave = true
}) {
  const {
    data: groups = [],
    isLoading,
    isError
  } = useQuery(
    ['partners', 'hierarchy-options'],
    async () => (await Axios.get('/partners/hierarchy-options')).data,
    { staleTime: 300000 }
  )
  const grade = values.hierarchicalGrade || ''
  const group =
    values.hierarchicalGroup ||
    groups.find((item) => item.grades.includes(grade))?.name ||
    ''
  const options = groups
    .filter((item) => !group || item.name === group)
    .flatMap((item) => item.grades)
  const groupOptions = [
    ...new Set([...groups.map((item) => item.name), ...(group ? [group] : [])])
  ]
  const gradeOptions = [...new Set([...options, ...(grade ? [grade] : [])])]
  return (
    <>
      <FormControl mb={3}>
        <FormLabel htmlFor="partner-contract">Tipo de contrato</FormLabel>
        <Select
          id="partner-contract"
          value={values.officialType || ''}
          bg="white"
          color="green.500"
          fontWeight={600}
          onChange={(e) => onChange({ officialType: e.target.value })}
        >
          <option value="">Sin definir</option>
          {[
            ...new Set([
              'UNIFORMADO',
              'CPR',
              'CIVIL PLANTA',
              'PROFESOR',
              'MEDICO',
              'JORNAL',
              ...(values.officialType ? [values.officialType] : [])
            ])
          ].map((c) => (
            <option key={c}>{c}</option>
          ))}
        </Select>
        <FormHelperText>
          {values.officialType === 'CPR'
            ? 'CPR: grado 0 $3.000; grados 1 a 12 $2.300; grados 13 en adelante $1.200.'
            : 'El contrato determina qué regla de descuento se aplica.'}
        </FormHelperText>
      </FormControl>
      {values.officialType === 'CPR' && (
        <FormControl mb={3}>
          <FormLabel htmlFor="cprGrade">Grado CPR</FormLabel>
          <Input
            id="cprGrade"
            type="number"
            min="0"
            step="1"
            bg="white"
            value={values.cprGrade ?? ''}
            onChange={(e) => onChange({ cprGrade: e.target.value })}
          />
          <FormHelperText>
            Grado numérico propio de CPR; no corresponde al grado jerárquico.
            {values.cprGradeSource === 'RESPONSE' &&
              ` Informado en la respuesta de ${values.cprGradePeriod}.`}
          </FormHelperText>
        </FormControl>
      )}
      {values.officialType !== 'CPR' && (
        <>
          {values.hierarchicalGroupSource === 'INFERRED' && (
            <Text mb={3} fontSize="sm" color="orange.700">
              Grupo estimado por el cobro de {values.hierarchicalGroupPeriod}.
              Falta confirmar el grado exacto.
            </Text>
          )}
          <FormControl mb={3}>
            <FormLabel htmlFor="hierarchicalGroup">Grupo jerárquico</FormLabel>
            <Select
              id="hierarchicalGroup"
              name="hierarchicalGroup"
              bg="white"
              color="green.500"
              fontWeight={600}
              focusBorderColor="green.100"
              value={group}
              isDisabled={isLoading || isError}
              onChange={(event) => {
                const selected = groups.find(
                  (item) => item.name === event.target.value
                )
                onChange({
                  hierarchicalGroup: selected?.name || '',
                  hierarchicalGrade: selected?.grades.includes(grade)
                    ? grade
                    : ''
                })
              }}
            >
              <option value="">Sin definir</option>
              {groupOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
            <FormHelperText>
              Puedes registrar el grupo aunque todavía no conozcas el grado
              exacto.
            </FormHelperText>
          </FormControl>
          <FormControl mb={3}>
            <FormLabel htmlFor="hierarchicalGrade">Grado jerárquico</FormLabel>
            <Select
              id="hierarchicalGrade"
              name="hierarchicalGrade"
              bg="white"
              color="green.500"
              fontWeight={600}
              focusBorderColor="green.100"
              value={grade}
              isDisabled={isLoading || isError}
              onChange={(event) => {
                const exact = event.target.value
                onChange({
                  hierarchicalGrade: exact,
                  hierarchicalGroup: exact
                    ? groups.find((item) => item.grades.includes(exact)).name
                    : group
                })
              }}
            >
              <option value="">Sin definir</option>
              {gradeOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
            <FormHelperText>
              Selecciona el grado solo cuando conozcas el valor exacto.
            </FormHelperText>
          </FormControl>
        </>
      )}
      {isError && (
        <Text color="red.600" fontSize="sm" mb={3}>
          No se pudo cargar el listado de grados. Recarga la vista para editar
          estos campos.
        </Text>
      )}
      {onSave && (
        <Button
          mb={4}
          size="sm"
          variant="outline"
          colorScheme="green"
          isLoading={isSaving}
          isDisabled={!canSave || isLoading || isError}
          onClick={onSave}
        >
          Guardar clasificación
        </Button>
      )}
    </>
  )
}
